// frontend/src/pages/AbbandoniPage.js
/**
 * "Abbandoni": pagamenti online non completati (ordini SumUp/PayPal non pagati) e carrelli lasciati sul sito.
 * Mostra i dati per contattare il cliente, i promemoria automatici inviati e cosa e' stato recuperato.
 * I promemoria partono da soli (cron orario): qui si possono anticipare o segnare un contatto fatto a mano.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Container, Card, Row, Col, Table, Badge, Button, Form, Tabs, Tab, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    getAbbandoni, inviaPromemoriaPagamento, segnaPagamentoContattato,
    inviaPromemoriaCarrello, segnaCarrelloContattato,
} from '../services/api';

const STATI_PAGAMENTO = {
    in_attesa: { testo: 'In attesa', bg: 'warning' },
    recuperato: { testo: 'Recuperato', bg: 'success' },
    pagato: { testo: 'Pagato', bg: 'success' },
    riordinato: { testo: 'Ha riordinato', bg: 'info' },
    annullato: { testo: 'Annullato', bg: 'secondary' },
};
const STATI_CARRELLO = {
    aperto: { testo: 'Aperto', bg: 'warning' },
    ordinato: { testo: 'Diventato ordine', bg: 'success' },
    scaduto: { testo: 'Scaduto', bg: 'secondary' },
};

const data = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const euro = (n) => (n == null ? '—' : `€ ${Number(n).toFixed(2).replace('.', ',')}`);
const errore = (e, testo) => toast.error(e?.response?.data?.detail || testo);

const Numero = ({ titolo, valore, nota }) => (
    <Card className="h-100 shadow-sm">
        <Card.Body>
            <div className="text-muted small">{titolo}</div>
            <div className="fs-3 fw-bold">{valore}</div>
            {nota && <div className="text-muted small">{nota}</div>}
        </Card.Body>
    </Card>
);

const Contatti = ({ riga }) => (
    <>
        <div>{riga.cliente || <span className="text-muted">—</span>}</div>
        {riga.email && <div className="small"><a href={`mailto:${riga.email}`}>{riga.email}</a></div>}
        {riga.telefono && <div className="small"><a href={`tel:${riga.telefono}`}>{riga.telefono}</a></div>}
    </>
);

const AbbandoniPage = () => {
    const [giorni, setGiorni] = useState(30);
    const [dati, setDati] = useState(null);
    const [caricamento, setCaricamento] = useState(true);
    const [inCorso, setInCorso] = useState(null);

    const carica = useCallback(async () => {
        setCaricamento(true);
        try {
            const res = await getAbbandoni(giorni);
            setDati(res.data);
        } catch (e) {
            errore(e, 'Impossibile caricare gli abbandoni');
        } finally {
            setCaricamento(false);
        }
    }, [giorni]);

    useEffect(() => { carica(); }, [carica]);

    const azione = async (chiave, fn, messaggio) => {
        setInCorso(chiave);
        try {
            await fn();
            if (messaggio) toast.success(messaggio);
            await carica();
        } catch (e) {
            errore(e, 'Operazione non riuscita');
        } finally {
            setInCorso(null);
        }
    };

    const n = dati?.numeri || {};

    return (
        <Container fluid className="py-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <div>
                    <h2 className="mb-0">Abbandoni</h2>
                    <div className="text-muted small">
                        Promemoria automatici: pagamento dopo 3 ore, carrello dopo 4 ore (solo con consenso o a chi ha già comprato).
                        Gli ordini non pagati si annullano dopo 48 ore.
                    </div>
                </div>
                <Form.Select style={{ width: 'auto' }} value={giorni} onChange={(e) => setGiorni(Number(e.target.value))}>
                    <option value={7}>Ultimi 7 giorni</option>
                    <option value={30}>Ultimi 30 giorni</option>
                    <option value={90}>Ultimi 90 giorni</option>
                </Form.Select>
            </div>

            {caricamento && !dati ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : !dati ? (
                <Alert variant="danger">Dati non disponibili.</Alert>
            ) : (
                <>
                    <Row className="g-3 mb-3">
                        <Col xs={6} md={3}><Numero titolo="Pagamenti non completati" valore={n.pagamenti_abbandonati} nota={`${n.pagamenti_promemoria} promemoria inviati`} /></Col>
                        <Col xs={6} md={3}><Numero titolo="Recuperati dal promemoria" valore={n.pagamenti_recuperati} nota={euro(n.euro_recuperati)} /></Col>
                        <Col xs={6} md={3}><Numero titolo="Carrelli lasciati" valore={n.carrelli} nota={`${n.carrelli_promemoria} promemoria inviati`} /></Col>
                        <Col xs={6} md={3}><Numero titolo="Carrelli diventati ordine" valore={n.carrelli_ordinati} /></Col>
                    </Row>

                    <Tabs defaultActiveKey="pagamenti" className="mb-3">
                        <Tab eventKey="pagamenti" title={`Pagamenti non completati (${dati.pagamenti.length})`}>
                            <Table responsive hover size="sm" className="bg-white align-middle">
                                <thead>
                                    <tr>
                                        <th>Ordine</th><th>Creato</th><th>Cliente</th><th>Articoli</th><th className="text-end">Totale</th>
                                        <th>Stato</th><th>Promemoria</th><th>Contattato</th><th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dati.pagamenti.length === 0 && (
                                        <tr><td colSpan={9} className="text-center text-muted py-4">Nessun pagamento abbandonato nel periodo.</td></tr>
                                    )}
                                    {dati.pagamenti.map((p) => {
                                        const st = STATI_PAGAMENTO[p.stato] || { testo: p.stato, bg: 'secondary' };
                                        return (
                                            <tr key={p.ordine_id}>
                                                <td><Link to={`/ordini/${p.ordine_id}`}>#{p.ordine_id}</Link><div className="small text-muted">{p.metodo}</div></td>
                                                <td className="small">{data(p.creato_il)}</td>
                                                <td><Contatti riga={p} /></td>
                                                <td className="small">{p.articoli.join(', ')}</td>
                                                <td className="text-end">{euro(p.totale)}</td>
                                                <td><Badge bg={st.bg}>{st.testo}</Badge></td>
                                                <td className="small">{data(p.promemoria_il)}</td>
                                                <td className="small">{data(p.contattato_il)}</td>
                                                <td className="text-nowrap">
                                                    {p.stato === 'in_attesa' && !p.promemoria_il && (
                                                        <Button size="sm" variant="outline-primary" className="me-1" disabled={inCorso !== null}
                                                            onClick={() => azione(`pp${p.ordine_id}`, () => inviaPromemoriaPagamento(p.ordine_id), 'Promemoria inviato')}>
                                                            {inCorso === `pp${p.ordine_id}` ? <Spinner size="sm" animation="border" /> : 'Invia promemoria'}
                                                        </Button>
                                                    )}
                                                    <Button size="sm" variant={p.contattato_il ? 'outline-secondary' : 'outline-success'} disabled={inCorso !== null}
                                                        onClick={() => azione(`pc${p.ordine_id}`, () => segnaPagamentoContattato(p.ordine_id))}>
                                                        {p.contattato_il ? 'Annulla contatto' : 'Contattato'}
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </Tab>

                        <Tab eventKey="carrelli" title={`Carrelli lasciati (${dati.carrelli.length})`}>
                            <Table responsive hover size="sm" className="bg-white align-middle">
                                <thead>
                                    <tr>
                                        <th>Ultima modifica</th><th>Cliente</th><th>Articoli</th><th className="text-end">Valore</th>
                                        <th>Stato</th><th>Promemoria</th><th>Contattato</th><th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dati.carrelli.length === 0 && (
                                        <tr><td colSpan={8} className="text-center text-muted py-4">Nessun carrello lasciato nel periodo.</td></tr>
                                    )}
                                    {dati.carrelli.map((k) => {
                                        const st = STATI_CARRELLO[k.stato] || { testo: k.stato, bg: 'secondary' };
                                        return (
                                            <tr key={k.id}>
                                                <td className="small">{data(k.aggiornato_il)}<div className="text-muted">{k.origine === 'account' ? 'account' : 'checkout'}</div></td>
                                                <td><Contatti riga={k} /></td>
                                                <td className="small">{k.articoli.length ? k.articoli.join(', ') : <span className="text-muted">non più disponibili</span>}</td>
                                                <td className="text-end">{euro(k.totale)}</td>
                                                <td>
                                                    <Badge bg={st.bg}>{st.testo}</Badge>
                                                    {k.ordine_id && <div className="small"><Link to={`/ordini/${k.ordine_id}`}>#{k.ordine_id}</Link></div>}
                                                </td>
                                                <td className="small">
                                                    {data(k.promemoria_il)}
                                                    {!k.promemoria_il && <div className="text-muted" title={k.motivo_promemoria}>{k.promemoria_possibile ? 'consentito' : 'non consentito'}</div>}
                                                </td>
                                                <td className="small">{data(k.contattato_il)}</td>
                                                <td className="text-nowrap">
                                                    {k.stato === 'aperto' && !k.promemoria_il && k.promemoria_possibile && (
                                                        <Button size="sm" variant="outline-primary" className="me-1" disabled={inCorso !== null}
                                                            onClick={() => azione(`kp${k.id}`, () => inviaPromemoriaCarrello(k.id), 'Promemoria inviato')}>
                                                            {inCorso === `kp${k.id}` ? <Spinner size="sm" animation="border" /> : 'Invia promemoria'}
                                                        </Button>
                                                    )}
                                                    <Button size="sm" variant={k.contattato_il ? 'outline-secondary' : 'outline-success'} disabled={inCorso !== null}
                                                        onClick={() => azione(`kc${k.id}`, () => segnaCarrelloContattato(k.id))}>
                                                        {k.contattato_il ? 'Annulla contatto' : 'Contattato'}
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </Tab>
                    </Tabs>
                </>
            )}
        </Container>
    );
};

export default AbbandoniPage;
